import { useCurrentStateAndParams } from '@uirouter/react';
import { useEffect, useCallback } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { editModelAtom, editPageStateAtom, readOnlyModelAtom } from '../vendor_edit_atoms';
import { modelVM, pageStateVM } from '../vendor_edit_view_models';
import { PageInit } from '../vendor_edit_types';
import { getDetail } from '../../data/vendorRepo';
import { ngContext } from '../../../../core/services/ng.delegate';

export const useVendorEditInit = (): PageInit => {
    const ctx = ngContext();
    const [state, setState] = useImmerAtom(editPageStateAtom);
    const setModel = useSetImmerAtom(editModelAtom);
    const setReadOnlyModel = useSetImmerAtom(readOnlyModelAtom);
    const route = useCurrentStateAndParams();

    const isVerity = ctx.User?.Type === 'VERITY';

    const getModel = useCallback(
        async (id: string): Promise<void> => {
            try {
                const r = await getDetail(id, state.isTPA ? 'TPAID' : 'VENID');
                const model = modelVM(r);

                setModel(() => model);
                setReadOnlyModel(() => model);
            } catch (err: any) {
                console.error(err);
                // handled upstream, need a catch when using a try/finally
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
            }
        },
        [setModel, setState, setReadOnlyModel, state.isTPA],
    );

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init) {
                // get context from Angular
                const ctx = ngContext();
                draft.isVeritySA = ctx.User?.Type == 'VERITYSA';

                draft.canEdit = true;

                // get mode / title / params from route
                if (!draft.mode) {
                    draft.mode = 'UPDATE';
                }
                draft.id = draft.mode == 'UPDATE' && !!route.params ? route.params.Id : '';

                // everything is settled off route
                draft.init = false;
                return draft;
            } else {
                return draft;
            }
        });

        // destroy
        return (): void => {
            setState(() => pageStateVM());
        };
    }, [route, setState]);

    useEffect(() => {
        setState(draft => {
            if (draft.mode == 'CREATE') {
                setModel(() => modelVM({ ObjectState: 'ACTIVE', Type: 'WHOLESALER' }, isVerity));
                draft.waiting = false;
            } else {
                draft.waiting = true;
                getModel(state.id);
            }
        });

        return (): void => {
            setModel(() => modelVM());
            setReadOnlyModel(() => modelVM());
        };
    }, [state.id, setState, setModel, getModel, setReadOnlyModel, state.mode, isVerity]);

    return state.title;
};
